import { type ComponentType, useCallback, useEffect, useRef } from 'react';
import { Wizard } from 'react-use-wizard';
import { useSelector } from 'react-redux';

import { selectFeatureFlags, selectClient } from '@client/model/selectors';
import { GuidedPlanningProvider, useIsSessionComplete, useSession } from './context';
import { GuidedPlanningHeader } from './components/guided-planning-header';
import { WizardAnimatedStep } from './components/animated-step';
import { QuotesPaywallGate } from './components/steps/quotes-paywall-gate';
import {
  FundingPlanOverviewStep,
  AssetSelectionStep,
  AssessmentIntroStep,
  AssessmentAccountsStep,
  AssessmentProjectionStep,
  PreferencesDeathBenefitsStep,
  PreferencesBenefitStructureStep,
  QuotesLoadingStep,
  QuotesSelectionStep,
  CcrcTypeASelectionStep,
  CcrcTypeCFeasibilityStep,
  QuotesFundingStep,
  PlanComparisonStep,
} from './components/steps';
import { getFlatStepConfigs, getGuidedPlanningVariant, type GuidedPlanningVariant } from './config';
import { useWizardStepSync } from './hooks';
import { analytics } from '@shared/posthog';

interface GuidedFinancialPlanningPageProps {
  clientId: string;
}

const STEP_COMPONENTS: Record<string, ComponentType> = {
  'overview': FundingPlanOverviewStep,
  'assets': AssetSelectionStep,
  'assessment-intro': AssessmentIntroStep,
  'assessment-accounts': AssessmentAccountsStep,
  'assessment-projection': AssessmentProjectionStep,
  'preferences-death-benefits': PreferencesDeathBenefitsStep,
  'preferences-benefit-structure': PreferencesBenefitStructureStep,
  'quotes-loading': QuotesLoadingStep,
  'quotes-selection': QuotesSelectionStep,
  'quotes-funding': QuotesFundingStep,
  'plan-comparison': PlanComparisonStep,
};

const STEP_COMPONENT_OVERRIDES: Partial<
  Record<GuidedPlanningVariant, Record<string, ComponentType>>
> = {
  'ccrc-type-a': { 'quotes-selection': CcrcTypeASelectionStep },
};

function resolveStepComponent(
  variant: GuidedPlanningVariant,
  financialFeasibility: boolean,
  stepId: string,
): ComponentType {
  if (financialFeasibility && stepId === 'assessment-projection') {
    return CcrcTypeCFeasibilityStep;
  }

  const component = STEP_COMPONENT_OVERRIDES[variant]?.[stepId] ?? STEP_COMPONENTS[stepId];

  if (!component) {
    throw new Error(`No guided planning step component for "${stepId}"`);
  }

  return component;
}

function GuidedPlanningWizard() {
  const { startIndex, onStepChange } = useWizardStepSync();
  const featureFlags = useSelector(selectFeatureFlags);
  const client = useSelector(selectClient);
  const { session, isLoading } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const sessionStartedRef = useRef(false);
  const variant = getGuidedPlanningVariant(featureFlags);
  const financialFeasibility = !!featureFlags.financialFeasibility;
  const genworthMode = !!featureFlags.genworthMode;
  const stepConfigs = getFlatStepConfigs(variant, financialFeasibility, genworthMode);

  //Track guided planning start on mount (sequential logs when journey is finished in multiple sessions)
  useEffect(() => {
    if (isLoading || !session) return;

    if (!isSessionComplete && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
      analytics.guidedPlanning.started({
        clientId: client.clientId,
        variant,
        stepCount: stepConfigs.length,
        hasPartner: !!client.mutableClientPartner?.clientId,
      });
    } else if (isSessionComplete) {
      analytics.guidedPlanning.completed({ clientId: client.clientId });
    }
  }, [isLoading, isSessionComplete, session]);

  const handleStepChange = useCallback(
    (newStepIndex: number) => {
      analytics.guidedPlanning.stepViewed({
        clientId: client.clientId,
        stepId: stepConfigs[newStepIndex].id,
      });
      onStepChange(newStepIndex);
    },
    [client.clientId, stepConfigs, onStepChange, analytics.guidedPlanning.stepViewed],
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Wizard
        startIndex={startIndex}
        header={<GuidedPlanningHeader />}
        onStepChange={handleStepChange}
        wrapper={<WizardAnimatedStep />}
      >
        {stepConfigs.map(config => {
          const StepComponent = resolveStepComponent(variant, financialFeasibility, config.id);

          if (config.id === 'quotes-selection') {
            return (
              <QuotesPaywallGate key={config.id}>
                <StepComponent />
              </QuotesPaywallGate>
            );
          }
          return <StepComponent key={config.id} />;
        })}
      </Wizard>
    </div>
  );
}

export function GuidedFinancialPlanningPage({ clientId }: GuidedFinancialPlanningPageProps) {
  return (
    <GuidedPlanningProvider clientId={clientId}>
      <GuidedPlanningWizard />
    </GuidedPlanningProvider>
  );
}
